import { useEffect, useRef, useState } from "react"

import { useLocation, useNavigate } from "react-router-dom"

import type { ChatSessionModel } from "@/api/models"

import { useRoutes } from "@/contexts/use-routes"
import { ChatService } from "@/services/chat-service"

import { useSessionId } from "./use-session-id"

/**
 * Hook that syncs the URL sessionId with the ChatService's open session
 * This ensures data fetching works correctly when navigating to dynamic routes
 */
type UseSessionSyncOptions = {
  onSelectSession?: (session: ChatSessionModel) => void
  onClearSession?: () => void
  // True while the sessions query is still in flight. Used so we can mark
  // load-attempted even when the fetch resolves to an empty array — a new
  // user with an invalid sessionId in the URL would otherwise get stuck
  // because the `sessions.length > 0` heuristic below never trips.
  // Defaults to ``true`` (loading) so callers that don't supply the flag
  // keep the legacy "wait for non-empty sessions" behavior and never
  // redirect prematurely on a slow fetch.
  isLoadingSessions?: boolean
  // False makes the hook a no-op: it never opens, closes, selects or
  // redirects. A sidebar list that sits next to a page-level owner passes
  // false so only one instance drives the route (#3515).
  enabled?: boolean
}

export function useSessionSync(
  sessionType: string,
  sessions: ChatSessionModel[],
  options: UseSessionSyncOptions = {}
) {
  const { onSelectSession, onClearSession, isLoadingSessions = true, enabled = true } = options
  const [chatService] = useState<ChatService>(ChatService.getInstance())
  const { getRoutePath } = useRoutes()
  const currentSessionId = useSessionId()
  const navigate = useNavigate()
  const location = useLocation()
  const hasRedirectedRef = useRef<string | null>(null)
  const sessionsRef = useRef(sessions)
  sessionsRef.current = sessions
  const [sessionsLoadAttempted, setSessionsLoadAttempted] = useState(false)

  // Track when sessions have been loaded at least once. Two ways to flip:
  // a non-empty array (fast path on returning users), or the loading flag
  // transitioning to false (covers an empty result for new users).
  useEffect(() => {
    if (sessions.length > 0 || isLoadingSessions === false) {
      setSessionsLoadAttempted(true)
    }
  }, [sessions, isLoadingSessions])

  useEffect(() => {
    if (!enabled) return

    if (!currentSessionId) {
      chatService.closeSession(sessionType)
      hasRedirectedRef.current = null
      onClearSession?.()
      return
    }

    // Find the session with the current sessionId
    const targetSession = sessions.find((session) => session?.id === currentSessionId)

    if (!targetSession) {
      // Only redirect if:
      // 1. We have a sessionId in the URL
      // 2. Sessions have been loaded (or we've waited for them)
      // 3. We haven't already redirected for this sessionId
      //
      // We defer the redirect by a short window so a freshly-created
      // Deep Explorer session (agent_run persisted server-side moments
      // ago) has time to land in the next sessions fetch before we
      // bounce the user to the landing page (Thobias feedback 2026-05-11).
      if (sessionsLoadAttempted && hasRedirectedRef.current !== currentSessionId) {
        const targetIdAtScheduleTime = currentSessionId
        const timer = setTimeout(() => {
          // Read latest sessions via ref to avoid acting on a stale
          // closure if the array's contents change without changing
          // identity (e.g., in-place mutation from a sibling cache update).
          const stillMissing = !sessionsRef.current.some((s) => s?.id === targetIdAtScheduleTime)
          if (stillMissing && hasRedirectedRef.current !== targetIdAtScheduleTime) {
            hasRedirectedRef.current = targetIdAtScheduleTime
            void navigate(getRoutePath(sessionType), { replace: true })
          }
        }, 2500)
        return () => clearTimeout(timer)
      }
      return
    }

    // Reset redirect tracking when we find a valid session
    if (hasRedirectedRef.current === currentSessionId) {
      hasRedirectedRef.current = null
    }

    // Check if this session is already the open session to avoid unnecessary updates
    const currentOpenSession = chatService.getOpenSession(sessionType)
    const shouldForceSessionReload =
      typeof location.state === "object" &&
      location.state !== null &&
      "forceSessionReload" in location.state &&
      (location.state as { forceSessionReload?: boolean }).forceSessionReload === true

    if (currentOpenSession?.id === currentSessionId && !shouldForceSessionReload) {
      // ChatService is already open for this session (e.g. return from another
      // feature while ownership survived unmount). Still notify so callers can
      // clear response-unread via setCurrentSessionId for the same id.
      onSelectSession?.(targetSession)
      return
    }

    // Update the ChatService's open session
    if (currentOpenSession?.id !== currentSessionId) {
      chatService.openSession(sessionType, targetSession)
    }

    // Call the onSelectSession callback if provided (for chat state updates)
    if (onSelectSession) {
      onSelectSession(targetSession)
    }

    if (shouldForceSessionReload) {
      void navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
    }
  }, [
    enabled,
    currentSessionId,
    sessions,
    sessionType,
    chatService,
    onSelectSession,
    onClearSession,
    navigate,
    sessionsLoadAttempted,
    location.pathname,
    location.search,
    location.state,
  ])

  return {
    currentSessionId,
    isSessionLoaded: currentSessionId ? sessions.some((s) => s?.id === currentSessionId) : true,
  }
}
